import type { ExistsFilter, ExpandFilter, Filter, InFilter, OrFilter } from 'json-to-lucene'

/**
 * Placeholder id for the "Unassigned" option in estimator/user filters. It stands for records
 * with no owner (e.g. quotes whose owner was removed when the user was deactivated, or quotes
 * created through the public API without an owner). It is never a real user id.
 *
 * Do not change this value: it is persisted in saved dashboard state and report filters (and in
 * report row links), and a new value would silently stop matching those saved selections.
 */
export const UNASSIGNED_OWNER_ID = '__unassigned__'

export function isUnassignedOwnerId(id: unknown): boolean {
  return id === UNASSIGNED_OWNER_ID
}

function missingFilter(field: string): ExistsFilter {
  // mode: 'field' — the default 'value' mode would also match owner ids that are empty strings
  return { type: 'exists', id: field, mode: 'field', not: true }
}

/**
 * Builds the owner clause for a dashboard query. Without the Unassigned placeholder this is the
 * plain `expand` filter used before. With it, the clause also matches records where
 * `missingField` (default: the first field) is not set.
 */
export function buildOwnerFilter({
  fields,
  ids,
  missingField = fields[0]
}: {
  fields: string[]
  ids: string[]
  missingField?: string
}): ExpandFilter | OrFilter {
  const realIds = ids.filter((id) => !isUnassignedOwnerId(id))
  const expand: ExpandFilter = { type: 'expand', fields, value: realIds }

  if (realIds.length === ids.length) {
    return expand
  }

  return {
    type: 'or',
    value: [...(realIds.length ? [expand] : []), missingFilter(missingField)]
  }
}

/**
 * Rewrites a report `in` filter that holds the Unassigned placeholder into
 * `id IN [real ids] OR id missing`. Any other filter is returned unchanged.
 */
export function expandUnassignedInFilter<T extends Filter>(filter: T): T | OrFilter {
  if (filter?.type !== 'in' || filter.not) {
    return filter
  }

  const inFilter = filter as InFilter
  const values = inFilter.value ?? []
  const realValues = values.filter((value) => !isUnassignedOwnerId(value))

  if (realValues.length === values.length) {
    return filter
  }

  return {
    type: 'or',
    value: [...(realValues.length ? [{ ...inFilter, value: realValues }] : []), missingFilter(inFilter.id)]
  }
}
